import type React from 'react';

import { DeleteSourceGroupButton } from '@vlabs/monokit/features/delete-source-group';
import { PageHeader, usePageContext } from '@vlabs/monokit/shared/ui/page';
import { usePageAccess, withPermissions } from '@vlabs/permissions';
import { Control, Breadcrumb, Breadcrumbs } from '@vlabs/uikit';
import { generatePath, useHistory } from 'react-router-dom';

import { useSourceGroupPageTranslation } from './i18n';
import { routes } from '../../app/routes';

const DeleteSourceGroupButtonWithAccess = withPermissions('group', 'delete')(DeleteSourceGroupButton);

interface SourceGroupPageHeaderProps {
  groupId: string;
  groupName?: string;
  isDirty: boolean;
  isSaving: boolean;
  onSave: VoidFunction;
}

function SourceGroupPageHeader({
  groupId,
  groupName = undefined,
  isDirty,
  isSaving,
  onSave,
}: SourceGroupPageHeaderProps): React.JSX.Element {
  const { t } = useSourceGroupPageTranslation();
  const history = useHistory();
  const pageCtx = usePageContext();
  const canViewList = usePageAccess('streams_fs');

  const title = groupName ?? groupId;

  return (
    <PageHeader
      actions={[
        <Control.Button
          data-testid="save-group"
          disabled={!isDirty || isSaving}
          isLoading={isSaving}
          key="save-group"
          onClick={onSave}
          size={pageCtx.buttonSize}
        >
          {t('save')}
        </Control.Button>,
        <DeleteSourceGroupButtonWithAccess
          data-testid="delete-group"
          groupId={groupId}
          key="delete-group"
          onDeleted={() => history.push(generatePath(routes.sources.list))}
          size={pageCtx.buttonSize}
        />,
      ]}
      breadcrumbs={(
        <Breadcrumbs>
          <Breadcrumb
            caption={t('back_to_sources')}
            href={canViewList ? routes.sources.list : undefined}
          />
          <Breadcrumb caption={title} />
        </Breadcrumbs>
      )}
      title={title}
    />
  );
}

export { SourceGroupPageHeader };
